import type React from "react";
import { useState, useEffect, type ChangeEvent } from "react";

import { Label } from "./ui/label";
import { Input } from "./ui/input";

interface FormattedNumberInputProps {
	id: string;
	name: string;
	label: string;
	defaultValue?: number | string | null;
	required?: boolean;
	placeholder?: string;
	className?: string;
	onChange?: (value: string) => void;
}

const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({
	id,
	name,
	label,
	defaultValue,
	required = false,
	placeholder = "0.00",
	className = "",
	onChange,
}) => {
	const [displayValue, setDisplayValue] = useState<string>("");
	const [rawValue, setRawValue] = useState<string>("");

	// Format number with commas for display
	const formatNumber = (value: string | number): string => {
		if (!value) return "";
		const cleaned = value.toString().replace(/[^\d.]/g, "");
		const parts = cleaned.split(".");
		parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
		return parts.join(".");
	};

	// Parse formatted number back to raw number (keep decimal point)
	const parseNumber = (value: string): string => {
		// Only remove commas, keep decimal point and digits
		return value.replace(/,/g, "");
	};

	// Update internal state when defaultValue changes
	useEffect(() => {
		if (
			defaultValue !== null &&
			defaultValue !== undefined &&
			defaultValue !== ""
		) {
			const numValue =
				typeof defaultValue === "string"
					? Number.parseFloat(defaultValue)
					: defaultValue;
			if (!Number.isNaN(numValue)) {
				const formatted = formatNumber(numValue);
				const raw = numValue.toString();
				setDisplayValue(formatted);
				setRawValue(raw);
				// Don't call onChange here to avoid infinite loops
			}
		} else {
			// Handle empty/null defaultValue
			setDisplayValue("");
			setRawValue("");
		}
	}, [defaultValue]);

	// Update rawValue and notify parent
	const updateValue = (newRawValue: string) => {
		setRawValue(newRawValue);
		onChange?.(newRawValue);
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
		const input = e.target.value;

		if (input === "") {
			setDisplayValue("");
			updateValue("");
			return;
		}

		// Allow only digits, commas, and one decimal point
		const validInput = /^[\d,]*\.?\d*$/.test(input);
		if (!validInput) return;

		const raw = parseNumber(input);

		// Validate decimal places (max 2) and only one decimal point
		const decimalParts = raw.split(".");
		if (decimalParts.length > 2) return; // More than one decimal point
		if (decimalParts[1] && decimalParts[1].length > 2) return; // More than 2 decimal places

		// Handle display formatting - this is the key fix
		let formattedDisplay = input; // Start with the original input

		if (raw === ".") {
			// Just a decimal point - show it immediately
			formattedDisplay = ".";
		} else if (raw.endsWith(".")) {
			// Number ending with decimal point (e.g., "123.")
			const integerPart = raw.slice(0, -1);
			if (integerPart) {
				formattedDisplay = formatNumber(integerPart) + ".";
			} else {
				formattedDisplay = ".";
			}
		} else if (raw.includes(".")) {
			// Number with decimal places (e.g., "123.45")
			const parts = raw.split(".");
			formattedDisplay = formatNumber(parts[0]) + "." + parts[1];
		} else {
			// Regular integer number
			formattedDisplay = formatNumber(raw);
		}

		setDisplayValue(formattedDisplay);
		updateValue(raw);
	};

	const handleBlur = (): void => {
		if (rawValue && rawValue !== "") {
			// Remove trailing decimal point on blur
			let finalRaw = rawValue;
			if (finalRaw.endsWith(".")) {
				finalRaw = finalRaw.slice(0, -1);
			}

			if (finalRaw === "") {
				setDisplayValue("");
				updateValue("");
				return;
			}

			const numValue = Number.parseFloat(finalRaw);
			if (!Number.isNaN(numValue)) {
				const formatted = formatNumber(numValue.toFixed(2));
				const finalValue = numValue.toString();
				setDisplayValue(formatted);
				updateValue(finalValue);
			}
		}
	};

	return (
		<div className={className}>
			{label && (
				<Label htmlFor={id}>
					{label}
					{required && <span className="text-red-500"> *</span>}
				</Label>
			)}
			<Input
				id={id}
				type="text"
				value={displayValue}
				onChange={handleChange}
				onBlur={handleBlur}
				placeholder={placeholder}
				className="text-right"
				autoComplete="off"
				required={required}
			/>
			{/* Hidden input for form submission backup */}
			<input type="hidden" name={name} value={rawValue} readOnly />
		</div>
	);
};

export default FormattedNumberInput;
